import {
  calcularEjecucionPresupuesto,
  distribuirCostos,
  exportarCsv,
  type CostoTransaccion,
  type EjecucionPresupuesto,
  type PresupuestoLinea,
} from "@torre/costos";
import { expenseCostType, type BudgetLine, type Expense } from "@torre/expenses";
import { categoryLabel } from "./format";

// ---------------------------------------------------------------------------
// Puente entre Gastos y el motor `@torre/costos` (importado de AgroCloud,
// #607). Un gasto es una transacción de costo cuyo "centro de costo" y "tipo de
// gasto" es su categoría; un presupuesto es una línea por categoría y periodo.
// La aritmética (ejecución, sobregiro, % con base cero = null) es la del motor.
// ---------------------------------------------------------------------------

type ExpenseLike = Pick<Expense, "id" | "period" | "date" | "category" | "amountBase" | "description" | "source">;

type Priced = ExpenseLike & { amountBase: number };

const ORIGEN: Record<string, CostoTransaccion["origen"]> = {
  nomina: "labor",
  equipo: "labor",
  correo: "compra",
  manual: "compra",
  ia_comprobante: "compra",
  ia_estado_cuenta: "compra",
};

export function toCostos(expenses: readonly ExpenseLike[], language = "es"): CostoTransaccion[] {
  // Un gasto sin tipo de cambio no tiene monto en pesos: no entra al cálculo.
  return expenses.filter((e): e is Priced => e.amountBase != null).map((e) => ({
    id: e.id,
    periodo: e.period,
    fecha: e.date,
    centroCostoId: e.category,
    centroCostoNombre: categoryLabel(e.category, language),
    tipoGastoId: e.category,
    tipoGastoNombre: categoryLabel(e.category, language),
    origen: ORIGEN[e.source] ?? "otro",
    directo: true,
    descripcion: e.description,
    monto: e.amountBase,
  }));
}

export function toPresupuesto(lines: readonly BudgetLine[], language = "es"): PresupuestoLinea[] {
  return lines.map((l) => ({
    id: l.id,
    periodo: l.period,
    centroCostoId: l.category,
    centroCostoNombre: categoryLabel(l.category, language),
    montoPresupuestado: l.amount,
    notas: l.notes ?? null,
  }));
}

/**
 * Ejecución del presupuesto para un periodo ("2026" o "2026-09"). Un
 * presupuesto anual se compara contra el gasto de todo el año.
 */
export function budgetExecution(
  lines: readonly BudgetLine[],
  expenses: readonly ExpenseLike[],
  periodo: string,
  language = "es",
): EjecucionPresupuesto {
  return calcularEjecucionPresupuesto({
    lineas: toPresupuesto(budgetLinesFor(lines, periodo), language),
    transacciones: toCostos(expenses, language),
    periodo,
  });
}

/**
 * Líneas que cuentan para un periodo, sin contar dos veces una categoría:
 *  - Año: la línea anual de la categoría; si no hay, la suma de sus mensuales.
 *  - Mes: la línea de ese mes; si no hay, la anual prorrateada a 1/12.
 */
export function budgetLinesFor(lines: readonly BudgetLine[], periodo: string): BudgetLine[] {
  const year = periodo.slice(0, 4);
  const annual = new Map(lines.filter((l) => l.period === year).map((l) => [l.category, l]));
  if (periodo.length === 4) {
    return lines.filter((l) => l.period === year || (l.period.startsWith(`${year}-`) && !annual.has(l.category)));
  }
  const monthly = lines.filter((l) => l.period === periodo);
  const covered = new Set(monthly.map((l) => l.category));
  const prorated = [...annual.values()]
    .filter((l) => !covered.has(l.category))
    // Sin redondear: doce doceavos redondeados no suman la línea anual (100,000 → 99,999.96).
    // Quien muestra o suma el mes redondea al final.
    .map((l) => ({ ...l, period: periodo, amount: l.amount / 12 }));
  return [...monthly, ...prorated];
}

export function categoryDistribution(expenses: readonly ExpenseLike[], periodo: string | null, language = "es") {
  return distribuirCostos(toCostos(expenses, language), "tipoGasto", { periodo });
}

export function expensesCsv(rows: readonly Expense[], language = "es"): string {
  return exportarCsv(rows, [
    { key: "date", label: "Fecha" },
    { key: "vendor", label: "Comercio / proveedor" },
    { key: "category", label: "Categoría", value: (r) => categoryLabel(r.category, language) },
    { key: "costType", label: "Tipo de costo", value: (r) => (expenseCostType(r) === "fijo" ? (language.startsWith("en") ? "Fixed" : "Fijo") : "Variable") },
    { key: "description", label: "Para qué" },
    { key: "justification", label: "Por qué" },
    { key: "amount", label: "Monto" },
    { key: "currency", label: "Moneda" },
    { key: "exchangeRate", label: "Tipo de cambio" },
    { key: "amountBase", label: "Monto MXN" },
    { key: "paymentMethod", label: "Forma de pago" },
    { key: "cardLast4", label: "Tarjeta" },
    { key: "invoiceStatus", label: "Factura" },
    { key: "invoiceUuid", label: "Folio fiscal", value: (r) => r.invoice?.uuid ?? "" },
    { key: "source", label: "Origen" },
    { key: "status", label: "Estado" },
  ]);
}
